import React, { useRef, useCallback, useEffect } from 'react';
import { Rnd } from 'react-rnd';
import { Dialog as BPDialog } from '@blueprintjs/core';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Styles from './Dialog.module.scss';

const getAbsoluteHeight = element => {
  const styles = window.getComputedStyle(element);
  const margin =
    parseFloat(styles['marginTop']) + parseFloat(styles['marginBottom']);

  return Math.ceil(element.offsetHeight + margin);
};

export const DraggableDialog = ({
  children,
  className,
  isOpen,
  onClose,
  title,
  width: defaultDialogWidth = 500
}) => {
  const dialogRef = useRef();
  const rnrRef = useRef();

  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;

  const updateCalculatedSize = useCallback(() => {
    let currentHeight = 0;

    if (dialogRef.current) {
      currentHeight = getAbsoluteHeight(dialogRef.current);
    }

    if (!rnrRef.current) {
      return null;
    }

    const height = !currentHeight ? 'auto' : currentHeight;

    rnrRef.current.updateSize({
      width: Math.min(defaultDialogWidth, windowWidth),
      height
    });

    rnrRef.current.updatePosition({
      x: Math.max((windowWidth - defaultDialogWidth) / 2, 0),
      y: Math.max((windowHeight - height) / 2, 0)
    });
  }, [defaultDialogWidth, windowHeight, windowWidth]);

  useEffect(() => {
    updateCalculatedSize();
  }, [isOpen, updateCalculatedSize]);

  if (!isOpen) {
    return null;
  }

  // Copy of BP3 dialog
  return (
    <div className={Styles.fixed}>
      <Rnd
        ref={rnrRef}
        bounds="body"
        className={Styles.rnd}
        enableResizing={false}
        default={{
          x: Math.max((windowWidth - defaultDialogWidth) / 2, 0),
          y: Math.max(windowHeight / 2, 0),
          width: Math.min(defaultDialogWidth, windowWidth),
          height: 'auto'
        }}
        dragHandleClassName="bp3-dialog-container"
        cancel='.stop-drag'
      >
        <div className="bp3-dialog-container bp3-overlay-content" tabIndex="0">
          <div
            ref={dialogRef}
            className={classnames(
              Styles.dialog,
              Styles.draggable,
              className,
              'bp3-dialog response'
            )}
          >
            <div className="bp3-dialog-header">
              <h4 className="bp3-heading">{title}</h4>
              <button
                onClick={onClose}
                type="button"
                aria-label="Close"
                className="bp3-button bp3-minimal bp3-dialog-close-button"
              >
                <span
                  icon="small-cross"
                  className="bp3-icon bp3-icon-small-cross"
                >
                  <svg
                    data-icon="small-cross"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                  >
                    <path
                      d="M11.41 10l3.29-3.29c.19-.18.3-.43.3-.71a1.003 1.003 0 00-1.71-.71L10 8.59l-3.29-3.3a1.003 1.003 0 00-1.42 1.42L8.59 10 5.3 13.29c-.19.18-.3.43-.3.71a1.003 1.003 0 001.71.71l3.29-3.3 3.29 3.29c.18.19.43.3.71.3a1.003 1.003 0 00.71-1.71L11.41 10z"
                      fillRule="evenodd"
                    ></path>
                  </svg>
                </span>
              </button>
            </div>
            {children}
          </div>
        </div>
      </Rnd>
    </div>
  );
};

DraggableDialog.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  width: PropTypes.number,
  children: PropTypes.any,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
};

const Dialog = ({ children, className, ...rest }) => {
  return (
    <BPDialog
      backdropClassName={Styles.backdrop}
      className={classnames(Styles.dialog, className)}
      {...rest}
    >
      {children}
    </BPDialog>
  );
};

Dialog.propTypes = {
  className: PropTypes.string,
  children: PropTypes.any
};

export default Dialog;
